import {
  ABU,
  buildSubView,
  Color,
  Em,
  FormWrapper,
  modalConfirm,
  NormalDist,
  playContext,
  showMsg,
  TestPage,
} from "@client";
import { Style } from "@client/style";
import { css } from "@emotion/css";
import { NpcModel } from "@npc";
import { calcScore, NumberUtil, SceneConst, SubNamespace } from "@share";
import { Button, Form, Input, ModalFuncProps, Radio, Table } from "antd";
import React, { useContext, useEffect, useMemo, useState } from "react";

function Play({ playDone }: { playDone: () => void }) {
  const sceneContext = useContext(playContext);
  const {
    saveResult,
    params: { searchCost, buyInsurance_secondLimit },
    playerState: { insuranceList },
  } = sceneContext;
  const termList = useMemo(() => insuranceList.map((s) => NumberUtil.geneInsuranceTerms(s.utility)), []);
  const [seenList, setSeenList] = useState(SceneConst.buyInsurance.insurances.map(() => false)),
    setSeen = (i: number) => {
      const _seenList = seenList.slice();
      _seenList[i] = true;
      setSeenList(_seenList);
    };
  const [tradeIndex, setTradeIndex] = useState<number>(null!);
  useEffect(() => {
    showMsg(
      <>
        本轮保险条款浏览时间<Em>{buyInsurance_secondLimit ? `限制为${buyInsurance_secondLimit}秒` : "无限制"}</Em>。
      </>,
      {
        okText: "知道了",
      }
    ).then();
  }, []);
  if (tradeIndex != null) {
    return null;
  }
  return (
    <FormWrapper>
      <Table size="middle" pagination={false} dataSource={SceneConst.buyInsurance.insurances}>
        <Table.Column title="保险套餐" dataIndex="name" />
        <Table.Column title="限时开售" dataIndex="base" render={(base) => base || "是"} />
        <Table.Column
          title="操作"
          render={({ name, base }, _, i) => {
            const limitTime = buyInsurance_secondLimit && !base,
              disabled = seenList[i] && !base;
            return (
              <Button
                disabled={disabled}
                className={disabled ? Style.btnDisabledXS : Style.btnPrimaryXS}
                onClick={() => {
                  const { price, utility } = insuranceList[i];
                  const itemStyle = css`
                    font-size: 2rem;
                    color: ${Color.main};
                  `;
                  const confirmProps: ModalFuncProps = {
                    onOk: () => {
                      setSeen(i);
                      const confirmProps: ModalFuncProps = {
                        okText: "购买",
                        onOk: async () => {
                          setTradeIndex(i);
                          const totalSearchCost = seenList
                            .map((seen) => (seen ? searchCost : 0))
                            .reduce((m, n) => m + n);
                          await showMsg(
                            <p>
                              {!!totalSearchCost && (
                                <>
                                  花费<Em>{totalSearchCost}</Em>的搜寻成本后，
                                </>
                              )}
                              你以
                              <Em>{price}</Em>
                              的价格买到效用为<Em>{utility}</Em>的保险， 你本轮购买保险的净收益为
                              <Em>{~~(utility - price)}</Em>，恭喜你。
                            </p>,
                            {
                              okText: "知道了",
                            }
                          );
                          const utilityList = insuranceList.map((t) => t.utility);
                          saveResult({
                            score: calcScore(
                              NumberUtil.getMin(utilityList),
                              utilityList[i],
                              NumberUtil.getMax(utilityList)
                            ),
                            seenList,
                            tradeIndex,
                          });
                          playDone();
                        },
                        title: name,
                        content: (
                          <Form labelCol={{ style: { width: "8rem" } }}>
                            <Form.Item label="保险条款">
                              <ul style={{ marginLeft: "2rem" }}>
                                {termList[i].map(({ accident, price, q }) => (
                                  <li
                                    className={css`
                                      list-style: decimal;
                                    `}
                                  >
                                    针对<Em>{accident}</Em>事故 ，提供最高
                                    <Em>{price}</Em>的保额。(发生概率为
                                    <Em>{(q * 100).toFixed(2)}%</Em>)
                                  </li>
                                ))}
                              </ul>
                            </Form.Item>
                            <Form.Item label="套餐价格">
                              <span className={itemStyle}>{price}</span>
                            </Form.Item>
                            <Form.Item label="倒计时">
                              <span className={itemStyle}>
                                <CountDown time={limitTime ? buyInsurance_secondLimit : 0} />
                              </span>
                            </Form.Item>
                          </Form>
                        ),
                      };
                      const modal = modalConfirm(confirmProps);
                      if (buyInsurance_secondLimit) {
                        setTimeout(() => {
                          modal.destroy();
                        }, buyInsurance_secondLimit * 1e3);
                      }
                    },
                    content: (
                      <p>
                        你选择了
                        <Em>{name}</Em>，
                        {searchCost ? (
                          <>
                            是否支付<Em>{searchCost}</Em>
                            元的搜寻成本查看该组合信息?
                          </>
                        ) : (
                          <>本次搜寻无成本。</>
                        )}
                      </p>
                    ),
                  };
                  modalConfirm(confirmProps);
                }}
              >
                查看
              </Button>
            );
          }}
        />
      </Table>
    </FormWrapper>
  );
}

function Test({ done }: { done: (answers, answerScores) => void }) {
  return (
    <TestPage
      questions={[
        {
          Content: ({ inputProps }) => (
            <div>
              <p>
                当卖家使得产品信息的理解更加复杂时，买家的搜寻成本增加了。在同样的搜寻期望净收益下，买家进行搜寻难度升高了，搜寻意愿下降了。
              </p>
              <Radio.Group {...inputProps({ margin: ".5rem" })}>
                <Radio value={1}>A. 正确</Radio>
                <Radio value={2}>B. 错误</Radio>
              </Radio.Group>
            </div>
          ),
          Answer: () => <p>思考的成本也是搜寻成本的重要来源之一。答案为：正确。</p>,
          answer: [1],
        },
        {
          Content: ({ inputProps }) => (
            <div>
              <p>
                卖家可能通过各种方式限制搜寻时间。比如，告知买家报价在较短时间内有效，或者减少与买家的接触时间。在压力下，买家可能被迫做出更草率的决定，比如过度搜寻或者搜寻不足。
              </p>
              <Radio.Group {...inputProps({ margin: ".5rem" })}>
                <Radio value={1}>A. 正确</Radio>
                <Radio value={2}>B. 错误</Radio>
              </Radio.Group>
            </div>
          ),
          Answer: () => <p>有关压力如何影响消费者搜寻仍然是当下研究的热点问题。压力对消费者搜寻的作用可能是多重的。</p>,
          answer: [1],
        },
        {
          isAnalysis: true,
          Content: ({ inputProps }) => (
            <div>
              <p>
                你认为卖家增加产品的复杂程度、并且设置截止时间的行为还会对买家的搜寻和购买产生什么样的影响？本题为主观题，无正确的答案，只需说明理由。
              </p>
              <br />
              <Input.TextArea maxLength={2e3} rows={4} {...inputProps({ width: "100%" })} />
            </div>
          ),
        },
      ]}
    />
  );
}

function BuyInsuranceIntro4th() {
  const [n, setN] = useState(0),
    [v, setV] = useState(0);
  return (
    <div>
      4.体验一下：你可通过点击以下【体验搜寻】按钮，体验保险效用【V_insurance】的（随机）变化。
      <table className={Style.tableInIntro}>
        <tbody>
          <tr>
            <td rowSpan={2}>
              第<Em>{n}</Em>次搜寻
            </td>
            <td>保险效用</td>
          </tr>
          <tr>
            <td>
              <Em>{v}</Em>
            </td>
          </tr>
        </tbody>
      </table>
      <p>
        保险效用的随机的分布服从均值为
        <Em>{SceneConst.buyInsurance.utility.MEAN}</Em>，标准差为
        <Em>{SceneConst.buyInsurance.utility.STD}</Em>的<NormalDist />。
      </p>
      <div style={{ margin: "1rem", textAlign: "right" }}>
        <Button
          className={Style.btnPrimarySm}
          onClick={() => {
            setN(n + 1);
            setV(NumberUtil.normalDist(SceneConst.buyInsurance.utility));
          }}
        >
          体验搜寻
        </Button>
      </div>
    </div>
  );
}

function CountDown({ time }: { time?: number }): JSX.Element {
  if (!time) {
    return <>不限时</>;
  }
  const [t, setT] = useState(time);
  useEffect(() => {
    const interval = window.setInterval(() => {
      if (t <= 0) {
        window.clearInterval(interval);
      } else {
        setT((t) => t - 1);
      }
    }, 1e3);
    return () => window.clearInterval(interval);
  }, []);
  return <>{t}</>;
}

export const BuyInsuranceView = buildSubView({
  namespace: SubNamespace.BuyInsurance,
  bg: `${ABU}scene/bg/BuyInsurance.jpg`,
  label: "买保险",
  withSearch: true,
  npcModel: NpcModel.BuyInsurance,
  introMsg: () => {
    return [
      ...`1.\t你打算以【${SceneConst.buyInsurance.price.MAX}】的预算为自己购置购置一份保险，用以保障生命、财产的安全。你将需要从${SceneConst.buyInsurance.insurances.length}个保险项目中选择最合适的选项。
2.\t保险公司在设置保险套餐时，会提供对N种不同风险的保障。这些风险的发生概率分别为【q1,q2,q3...】，对应地，保额分别为【p1,p2,p3...】。因此，对你而言该套餐的效用为【V_insurance=p1*q1+p2*q2+p3*q3+...】`
        .trim()
        .split("\n"),
      <>
        3. 对于不同的服务组合，你从保险中获得的效用是随机的。假设你对于某保险的效用【V_insurance】服从均值为【
        {SceneConst.buyInsurance.utility.MEAN}】，标准差为【
        {SceneConst.buyInsurance.utility.STD}】的
        <NormalDist />。
      </>,
      <BuyInsuranceIntro4th />,
      ...`5.\t你在搜寻过程中会产生成本：每次搜寻需要花费【S_insurance】元。
6.\t你可以通过点击【查看】获得保险的具体价格以及该保险所涵盖的N种风险保障的概率和保额，你需要通过计算得出该保险的具体效用并决定是否接受该保险，或者选择继续搜寻。
7.\t保险公司提供的选项可能存在查看时间限制，即如果超过该决策时间，你将无法购买该保险，故你必须在【Second】秒内完成一次搜寻。另外，部分保险为限时开售，你将无法在首次放弃购买该保险后再次选择购买。
8.\t部分保险套餐不会限制查看次数与决策时间，您可以随时查看购买。
`
        .trim()
        .split("\n"),
    ];
  },
  npcWelcome: async () => {
    await showMsg("你的任务是在接下来的时间里，购买一份保险。购买保险可以让你免于工资收入的突然降低。");
  },
  Play,
  Test,
  initState: (s) =>
    (s.insuranceList = SceneConst.buyInsurance.insurances.map(() => ({
      price: NumberUtil.uniformDist(SceneConst.buyInsurance.price),
      utility: NumberUtil.normalDist(SceneConst.buyInsurance.utility),
    }))),
});
